import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Country } from 'country-state-city';
import { GlobalMap, TrendChart, Newsletters, RegulatoryCalendar } from './components';
import AppSidebar from '../layout/AppSidebar';
import SideColumn from './SideColumn';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSearchHistory } from '../../context/SearchHistoryContext';
import { AllRegulationsTable } from './AllRegulationsTable';
import { apiRequest } from '../../api/client';
import DashboardKPICards from './components/DashboardKPICards';
import OverviewPanel from './components/OverviewPanel';
import TrendingPanel from './components/TrendingPanel';
import AnalyticsPanel from './components/AnalyticsPanel';
import { useDashboardData } from './hooks/useDashboardData';
import { getDashboardConfig, type DashboardConfig } from '../../api/workspaceConfigApi';
import type { TimeRangeType, DateFilterType } from './hooks/useDashboardData';
import styles from './DashboardPage.module.css';
import DataFreshness from '../../components/DataFreshness';
import { entitledCodes } from '../../utils/jurisdictionScope';
import { fetchDashboardOverview } from '../../api/dashboardApi';
import { BRAND_ORANGE } from '../../design-system/chartColors';

// Re-export commonly-imported dashboard subcomponents for backward compatibility
export { GlobalMap, ActivityFeed, TrendChart, AlertsWidget, SavedSearches, AlertsTabsWidget } from './components';

const cn = clsx;

const DashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, token } = useAuth();
    const { theme } = useTheme();
    useSearchHistory();

    // ── UI state ──────────────────────────────────────────────────────────────
    const [activeFeedTab, setActiveFeedTab] = useState<'dashboard' | 'alerts' | 'all' | 'chart' | 'trending' | 'newsletters' | 'calendar'>('dashboard');
    // Workspace dashboard configuration (SRS #124): tabs, order, landing tab, widgets, note.
    const [wsConfig, setWsConfig] = useState<DashboardConfig | null>(null);
    const landedRef = useRef(false);
    useEffect(() => { getDashboardConfig().then((r) => setWsConfig(r.config)).catch(() => {}); }, []);
    useEffect(() => {
        if (!wsConfig || landedRef.current) return;
        landedRef.current = true;
        if ((location.pathname === '/dashboard' || location.pathname === '/dashboard/') && wsConfig.landing_tab !== 'dashboard') {
            const paths: Record<string, string> = { all: 'all-regulations', chart: 'chart', trending: 'trending', newsletters: 'newsletters', calendar: 'calendar', alerts: 'active-alerts' };
            navigate(`/dashboard/${encodeURIComponent(paths[wsConfig.landing_tab] || '')}`, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wsConfig]);
    const widgetOn = (w: keyof DashboardConfig['widgets']) => !wsConfig || wsConfig.widgets[w] !== false;
    const [selectedJurisdiction, setSelectedJurisdiction] = useState('All');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRangeType>('All');
    // Default the regulations Time Range to "date added" (discovery date) so
    // recently-ingested regulations show under Last 7 Days regardless of their
    // legislative action date. Other widgets gracefully fall back to action date.
    const [dateFilterType, setDateFilterType] = useState<DateFilterType>('date_added');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [selectedContinent] = useState('All');
    const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [, setJurisdictionDropdownOpen] = useState(false);
    const [jurisdictionSearch, setJurisdictionSearch] = useState('');
    const [showRightFilters] = useState(false);
    const jurisdictionDropdownRef = useRef<HTMLDivElement | null>(null);
    const [allCountryOptions, setAllCountryOptions] = useState<{ id: string; name: string; iso2: string }[]>([]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getDateRangeForAPI = (): { start_date?: string; end_date?: string; days?: number } => {
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        if (selectedTimeRange === 'Custom') return { start_date: customStartDate || undefined, end_date: customEndDate || undefined };
        if (selectedTimeRange === 'All') return {};
        const daysMap: Record<string, number> = { '24H': 1, '7D': 7, '30D': 30, '90D': 90, '1Y': 365 };
        const days = daysMap[selectedTimeRange] || 7;
        const start = new Date(now);
        start.setDate(start.getDate() - days);
        return { start_date: start.toISOString().split('T')[0], end_date: today };
    };

    const handleTimeRangeChange = (value: TimeRangeType) => {
        setSelectedTimeRange(value);
        setShowCustomDatePicker(value === 'Custom');
    };

    const handleLogout = () => { logout(); navigate('/login'); };

    // ── Data hook ─────────────────────────────────────────────────────────────
    const data = useDashboardData(token, {
        selectedJurisdiction, selectedTimeRange, dateFilterType,
        customStartDate, customEndDate, selectedCountry,
        selectedContinent, activeFeedTab, getDateRangeForAPI,
    });

    const {
        totalJurisdictions, totalRegulations, countriesTracked, newslettersCount,
        jurisdictions, jurisdictionStats, countryStats, availablePlaces, mapLayers,
        recentRegulations, trendData,
        availableJurisdictionTypes, availableRegulationTypes,
        jurisdictionFilters, setJurisdictionFilters,
        policyTypeFilters, setPolicyTypeFilters,
        isLoadingJurisdictions, isLoadingRegulations, isLoadingTrends,
        isLoadingStats,
        supranationalEntries, fetchRecentRegulations,
    } = data;

    // ── Load country list once for filtered country dropdown ──────────────────
    useEffect(() => {
        apiRequest<{ id: string; name: string; iso2: string }[]>('/api/v1/dashboard/countries')
            .then(setAllCountryOptions)
            .catch(() => {});
    }, []);

    // SRS #16: the jurisdictions this account is entitled to (names from the
    // overview scope) → ISO-2 codes for the map. null = unrestricted / anonymous.
    const [entitledNames, setEntitledNames] = useState<string[] | null>(null);
    useEffect(() => {
        if (!token) { setEntitledNames(null); return; }
        fetchDashboardOverview(token).then(o => setEntitledNames(o.scope.entitled_jurisdictions)).catch(() => setEntitledNames(null));
    }, [token]);
    const entitledIso = useMemo(() => entitledCodes(entitledNames, allCountryOptions), [entitledNames, allCountryOptions]);

    // Countries that actually have regulations (from countryStats, excluding supranationals)
    const filteredCountryOptions = useMemo(() => {
        if (!Object.keys(countryStats).length || !allCountryOptions.length) return allCountryOptions;
        return allCountryOptions.filter(c => {
            const iso = c.iso2?.toUpperCase();
            return iso && countryStats[iso] !== undefined && !!Country.getCountryByCode(iso);
        });
    }, [countryStats, allCountryOptions]);

    // ── Sync URL → tab ────────────────────────────────────────────────────────
    useEffect(() => {
        const path = location.pathname || '';
        if (!path.startsWith('/dashboard')) return;
        const label = decodeURIComponent(path.replace('/dashboard', '').replace(/^\//, ''));
        const map: Record<string, string> = {
            '': 'dashboard', 'active-alerts': 'alerts', 'active alerts': 'alerts',
            'all-regulations': 'all', 'all regulations': 'all', 'chart': 'chart', 'trending': 'trending', 'newsletters': 'newsletters', 'calendar': 'calendar',
        };
        setActiveFeedTab((map[label] ?? 'dashboard') as any);
    }, [location.pathname]);

    // ── Close dropdown on outside click ──────────────────────────────────────
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!jurisdictionDropdownRef.current?.contains(e.target as Node))
                setJurisdictionDropdownOpen(false);
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);

    // ── Admin reset-filters event ─────────────────────────────────────────────
    useEffect(() => {
        const handler = () => resetAllFilters();
        window.addEventListener('admin:reset-filters', handler as EventListener);
        return () => window.removeEventListener('admin:reset-filters', handler as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Derived ───────────────────────────────────────────────────────────────
    const trendingTopics = useMemo(
        () => recentRegulations.slice(0, 5).map(r => r.title || r.title_short || ''),
        [recentRegulations],
    );

    const hasActiveFilters = useMemo(() => {
        const someJuris = selectedJurisdiction !== 'All' || jurisdictionSearch.trim() !== '';
        const someCountry = !!selectedCountry;
        const someTime = selectedTimeRange !== 'All';
        const someJurType = Object.keys(jurisdictionFilters).some(k => !jurisdictionFilters[k]);
        const someRegType = Object.keys(policyTypeFilters).some(k => !policyTypeFilters[k]);
        return someJuris || someCountry || someTime || someJurType || someRegType;
    }, [selectedJurisdiction, jurisdictionSearch, selectedCountry, selectedTimeRange, jurisdictionFilters, policyTypeFilters]);

    const filteredPlaces = useMemo(() => {
        const q = jurisdictionSearch.trim().toLowerCase();
        if (!q) return availablePlaces;
        return availablePlaces.filter(p => p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q));
    }, [availablePlaces, jurisdictionSearch]);

    const resetAllFilters = () => {
        setSelectedJurisdiction('All');
        setSelectedLocation(null);
        setSelectedCountry('');
        setJurisdictionSearch('');
        setJurisdictionDropdownOpen(false);
        setSelectedTimeRange('All');
        const jf: Record<string, boolean> = {};
        availableJurisdictionTypes.forEach(t => { jf[t] = true; });
        setJurisdictionFilters(jf);
        const pf: Record<string, boolean> = {};
        availableRegulationTypes.forEach(t => { pf[t] = true; });
        setPolicyTypeFilters(pf);
        void fetchRecentRegulations({});
    };

    // ── All-regulations tab param computation ─────────────────────────────────
    const allRegsParams = useMemo(() => {
        const selRegTypes = policyTypeFilters ? Object.keys(policyTypeFilters).filter(k => policyTypeFilters[k]) : [];
        const regTypeParam = (!selRegTypes.length || selRegTypes.length === availableRegulationTypes.length) ? undefined : selRegTypes.join(',');
        const selJurTypes = jurisdictionFilters ? Object.keys(jurisdictionFilters).filter(k => jurisdictionFilters[k]) : [];
        const jurTypeParam = (selectedJurisdiction === 'All' && selJurTypes.length > 0 && selJurTypes.length < availableJurisdictionTypes.length) ? selJurTypes.join(',') : undefined;
        return { regTypeParam, jurTypeParam };
    }, [policyTypeFilters, jurisdictionFilters, availableRegulationTypes, availableJurisdictionTypes, selectedJurisdiction]);

    const shouldShowSideColumn = showRightFilters;

    const sharedSidebarProps = {
        token, selectedCountry, setSelectedCountry,
        jurisdictions, selectedJurisdiction, setSelectedJurisdiction,
        availableJurisdictionTypes, jurisdictionFilters, setJurisdictionFilters,
        availableRegulationTypes, policyTypeFilters, setPolicyTypeFilters,
        dateFilterType, setDateFilterType, selectedTimeRange, handleTimeRangeChange,
        newslettersCount,
        showCustomDatePicker, customStartDate, customEndDate,
        setCustomStartDate, setCustomEndDate,
        isLoadingJurisdictions,
        onClearFilters: resetAllFilters, hasActiveFilters,
    };

    return (
        <div className={cn(styles.page, false && styles.editingMode)}>
            <div className={cn(styles.monitoringLayout, !shouldShowSideColumn && styles.noSideColumn)}>
                {/* The one sidebar (features/layout/AppSidebar). This page used to
                    carry its own six-entry menu of dashboard views, which is why
                    the account that landed here could not reach Chat, Alerts,
                    Agents or anything else the plan included. The views are
                    routes — /dashboard syncs the tab from the path — so they are
                    menu entries like everything else now. */}
                <AppSidebar
                    footer={(
                        <button type="button" className={styles.sidebarSignOut} onClick={handleLogout}>
                            Sign out
                        </button>
                    )}
                />

                <main className={styles.mainColumn}>
                    <div className={styles.widgetGrid}>

                        {activeFeedTab === 'dashboard' && (
                            <section style={{ gridColumn: 'span 12', padding: 0, margin: 0, minHeight: 320 }}>
                                {wsConfig && (wsConfig.welcome_note || wsConfig.workspace_name) && (
                                    <div style={{ margin: '0 0 14px', padding: '12px 16px', borderRadius: 12, borderLeft: `4px solid ${wsConfig.accent || BRAND_ORANGE}`, background: 'var(--ds-surface, #fff)', border: '1px solid var(--ds-border, #eaecf0)' }}>
                                        {wsConfig.workspace_name && <div style={{ fontWeight: 800, fontSize: 15 }}>{wsConfig.workspace_name}</div>}
                                        {wsConfig.welcome_note && <div style={{ fontSize: 13.5, color: 'var(--color-text-muted, #667085)', marginTop: 2 }}>{wsConfig.welcome_note}</div>}
                                    </div>
                                )}
                                {/* Charts first: KN asked for the dashboard to open on them
                                    rather than on the coverage header. */}
                                {widgetOn('analysis') && <AnalyticsPanel token={token} theme={theme} />}

                                {widgetOn('coverage') && <OverviewPanel token={token} />}
                                {widgetOn('kpis') && <DashboardKPICards
                                    totalJurisdictions={totalJurisdictions}
                                    totalRegulations={totalRegulations}
                                    countriesTracked={countriesTracked}
                                    supranationalEntries={supranationalEntries}
                                    onActivePoliciesClick={() => navigate('/dashboard/regulations-breakdown')}
                                />}
                                {/* ------------------------------------------------------------------
                                    Regulatory world map — commented out on 5 Oct at KN's request,
                                    kept here rather than deleted so it can be restored by
                                    uncommenting. The charts below took its place on this view.
                                ------------------------------------------------------------------ */}
                                {/*
                                {widgetOn('map') && (<>
                                <div className={styles.widgetHeader}>
                                    <div>
                                        <div className={styles.widgetEyebrow}>Global Coverage</div>
                                        <h2>Regulatory world map</h2>
                                    </div>
                                    <div className={styles.widgetSubtitle}><DataFreshness compact /></div>  {/* SRS #15 * /}
                                </div>
<GlobalMap
                                    stats={jurisdictionStats}
                                    countryStats={countryStats}
                                    layers={mapLayers?.layers}
                                    layerLabels={mapLayers?.labels}
                                    isLoading={isLoadingStats}
                                    allowedCountryCodes={entitledIso}
                                    selectedLocation={selectedLocation}
                                    onCountryClick={(code, name) => {
                                        // Map emits ISO-2; the country filter expects the countries-table UUID.
                                        const opt = allCountryOptions.find(c => c.iso2?.toUpperCase() === code.toUpperCase());
                                        if (opt) { setSelectedCountry(opt.id); setActiveFeedTab('all'); return; }
                                        // Supranational (EU…): filter by the jurisdiction instead (SRS #16).
                                        const j = jurisdictions.find(x => (x.code || '').toUpperCase() === code.toUpperCase() || x.name === name);
                                        if (j) { setSelectedJurisdiction(j.id); setActiveFeedTab('all'); }
                                    }}
                                    onLockedClick={(_code, name) => navigate('/pricing', { state: { changeOnly: true, reason: `${name} is not in your plan` } })}
                                />
                                </>)}
*/}

                                {/* The charts KN asked to see here. They already existed on the
                                    Chart view, where nobody looking at the dashboard found them:
                                    activity over 12 months, the status mix as a donut, the top
                                    jurisdictions and the type breakdown. */}

                            </section>
                        )}

                        {activeFeedTab === 'trending' && (
                            <section style={{ gridColumn: 'span 12', padding: '0' }}>
                                <TrendingPanel
                                    token={token}
                                    theme={theme}
                                    onJurisdiction={(name) => {
                                        const match = jurisdictions.find(j => j.name === name);
                                        if (match) setSelectedJurisdiction(match.id);
                                        setActiveFeedTab('all');
                                    }}
                                />
                            </section>
                        )}

                        {activeFeedTab === 'newsletters' && (
                            <section style={{ gridColumn: 'span 12', padding: '0' }}>
                                <Newsletters />
                            </section>
                        )}

                        {activeFeedTab === 'calendar' && (   /* SRS #11–#14 */
                            <section className={cn(styles.widgetCard)} style={{ gridColumn: 'span 12' }}>
                                <RegulatoryCalendar />
                            </section>
                        )}

                        {activeFeedTab === 'all' && (
                            <section style={{ gridColumn: 'span 12', padding: '0 0.1rem' }}>
                                <div className={styles.inlineFilterBar}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
                                        <label style={{ fontWeight: 500, fontSize: '0.95rem', marginBottom: 4, display: 'block' }}>Country</label>
                                        <select
                                            value={selectedCountry}
                                            onChange={e => setSelectedCountry(e.target.value)}
                                            className={styles.inlineFilterSelect}
                                        >
                                            <option value="">All Countries</option>
                                            {filteredCountryOptions.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                            {supranationalEntries.length > 0 && (
                                                <optgroup label="Supranational Bodies">
                                                    {supranationalEntries.map(s => (
                                                        <option key={s.code} value={`__supra__${s.code}`}>{s.name}</option>
                                                    ))}
                                                </optgroup>
                                            )}
                                        </select>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
                                        <label style={{ fontWeight: 500, fontSize: '0.95rem', marginBottom: 4, display: 'block' }} htmlFor="all-jurisdiction-select">Jurisdiction</label>
                                        <select id="all-jurisdiction-select" value={selectedJurisdiction} onChange={e => setSelectedJurisdiction(e.target.value)} disabled={isLoadingJurisdictions} className={styles.inlineFilterSelect}>
                                            <option value="All">All Jurisdictions</option>
                                            {(selectedCountry?.startsWith('__supra__')
                                                ? jurisdictions?.filter(j => {
                                                    const prefix = selectedCountry.replace('__supra__', '');
                                                    return (j.code || '').toUpperCase().startsWith(prefix + '-') || (j.code || '').toUpperCase() === prefix;
                                                  })
                                                : jurisdictions
                                            )?.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
                                        </select>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
                                        <label style={{ fontWeight: 500, fontSize: '0.95rem', marginBottom: 4, display: 'block' }} htmlFor="all-time-range-select">Time Range</label>
                                        <select id="all-time-range-select" value={selectedTimeRange} onChange={e => handleTimeRangeChange(e.target.value as TimeRangeType)} className={styles.inlineFilterSelect}>
                                            <option value="All">All Time</option>
                                            <option value="24H">Last 24 Hours</option>
                                            <option value="7D">Last 7 Days</option>
                                            <option value="30D">Last 30 Days</option>
                                            <option value="90D">Last 90 Days</option>
                                            <option value="1Y">Last Year</option>
                                            <option value="Custom">Custom Range</option>
                                        </select>
                                    </div>
                                    {selectedTimeRange === 'Custom' && (
                                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                                            <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} style={{ padding: '7px 10px', borderRadius: 7, border: `1px solid ${theme === 'dark' ? '#344054' : '#eaecf0'}`, background: theme === 'dark' ? '#1d2939' : 'var(--ds-surface-2)', color: theme === 'dark' ? '#eaecf0' : '#101828', fontSize: 13 }} />
                                            <span style={{ alignSelf: 'center', color: 'var(--ds-text-muted)', fontSize: 12 }}>to</span>
                                            <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} style={{ padding: '7px 10px', borderRadius: 7, border: `1px solid ${theme === 'dark' ? '#344054' : '#eaecf0'}`, background: theme === 'dark' ? '#1d2939' : 'var(--ds-surface-2)', color: theme === 'dark' ? '#eaecf0' : '#101828', fontSize: 13 }} />
                                        </div>
                                    )}
                                    {hasActiveFilters && (
                                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <button onClick={resetAllFilters} style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid ${theme === 'dark' ? '#344054' : '#eaecf0'}`, background: 'transparent', color: theme === 'dark' ? '#98a2b3' : '#667085', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Clear All</button>
                                        </div>
                                    )}
                                </div>
                                <AllRegulationsTable
                                    jurisdictions={jurisdictions}
                                    jurisdictionId={
                                        selectedCountry?.startsWith('__supra__')
                                            ? selectedCountry.replace('__supra__', '')
                                            : selectedJurisdiction !== 'All' ? selectedJurisdiction : undefined
                                    }
                                    jurisdictionType={allRegsParams.jurTypeParam}
                                    countryId={selectedCountry && !selectedCountry.startsWith('__supra__') ? selectedCountry : undefined}
                                    regulationType={allRegsParams.regTypeParam}
                                    isLoading={isLoadingRegulations}
                                    selectedTimeRange={selectedTimeRange}
                                    dateFilterType={dateFilterType}
                                    customStartDate={customStartDate}
                                    customEndDate={customEndDate}
                                />
                            </section>
                        )}

                        {activeFeedTab === 'chart' && (
                            <section className={cn(styles.widgetCard)} style={{ gridColumn: 'span 12', background: 'transparent', border: 'none', padding: '0', boxShadow: 'none', minHeight: 'auto' }}>
                                <div style={{ marginBottom: 18 }}><AnalyticsPanel token={token} theme={theme} /></div>
                                <TrendChart data={trendData} isLoading={isLoadingTrends} totalRegulations={totalRegulations ?? undefined} jurisdictionStats={jurisdictionStats} countryStats={countryStats} />
                            </section>
                        )}

                    </div>
                </main>

                {(['alerts'].includes(activeFeedTab) || showRightFilters) && (
                    <SideColumn
                        {...sharedSidebarProps}
                        trendingTopics={trendingTopics}
                        activeFeedTab={activeFeedTab}
                        showFiltersOverride={showRightFilters}
                    />
                )}
            </div>
        </div>
    );
};

export default DashboardPage;
